import { ErrorMessage } from "@hookform/error-message"
import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material"
import { useMemo } from "react"
import { Controller, RegisterOptions, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaChevronDown } from "react-icons/fa6"

export interface CategoryGroup {
  categoryId: number
  categoryName: string
  propertyIds: number[]
}

export interface PropertyItem {
  value: string
  label: string
}

interface IProps {
  name: string
  registerOptions?: RegisterOptions
  label?: string
  categoryGroups: CategoryGroup[]
  allProperties: PropertyItem[]
  withOutErrorMessage?: boolean
}

const SELECT_ALL_PREFIX = "__SELECT_ALL_CAT__"

export default function GroupedPropertySelect({
  name,
  registerOptions,
  label,
  categoryGroups,
  allProperties,
  withOutErrorMessage,
}: IProps) {
  const { control, formState, watch } = useFormContext()
  const { errors } = formState
  const { t, i18n: { language } } = useTranslation()

  const selectedData: string[] = watch(name) ?? []

  const isRequired =
    typeof registerOptions?.required === "object"
      ? registerOptions.required.value
      : !!registerOptions?.required

  const categorizedIds = new Set(
    categoryGroups.flatMap((g) => g.propertyIds.map(String))
  )
  const uncategorizedProps = allProperties.filter(
    (p) => !categorizedIds.has(p.value)
  )

  const helperText = useMemo(() => {
    if (!selectedData.length) return null

    for (const group of categoryGroups) {
      const catVals = group.propertyIds.map(String)
      if (
        catVals.length > 0 &&
        catVals.length === selectedData.length &&
        catVals.every((v) => selectedData.includes(v))
      ) {
        return {
          ar: `جميع وحدات "${group.categoryName}" محددة (${catVals.length} وحدة)`,
          en: `All units of "${group.categoryName}" selected (${catVals.length} unit${catVals.length !== 1 ? "s" : ""})`,
        }
      }
    }

    if (selectedData.length === 1) {
      const prop = allProperties.find((p) => p.value === selectedData[0])
      const propName = prop?.label || selectedData[0]
      return {
        ar: `تم تحديد وحدة واحدة: ${propName}`,
        en: `Single property selected: ${propName}`,
      }
    }

    return null
  }, [selectedData, categoryGroups, allProperties])

  const handleChange = (
    event: SelectChangeEvent<string[]>,
    onChange: (v: string[]) => void
  ) => {
    const raw = event.target.value
    const values: string[] =
      typeof raw === "string" ? raw.split(",") : (raw as string[])

    const selectAllEntry = values.find((v) => v.startsWith(SELECT_ALL_PREFIX))
    if (selectAllEntry) {
      const catId = parseInt(selectAllEntry.replace(SELECT_ALL_PREFIX, ""), 10)
      const group = categoryGroups.find((g) => g.categoryId === catId)
      if (group) {
        const catVals = group.propertyIds.map(String)
        const current = values.filter((v) => !v.startsWith(SELECT_ALL_PREFIX))
        const allChosen = catVals.every((v) => current.includes(v))
        const next = allChosen
          ? current.filter((v) => !catVals.includes(v))
          : Array.from(new Set([...current, ...catVals]))
        onChange(next)
        return
      }
    }

    const unique = Array.from(
      new Set(values.filter((v) => !v.startsWith(SELECT_ALL_PREFIX)))
    )
    onChange(unique)
  }

  return (
    <div>
      <label>
        {label && (
          <p style={{ marginBottom: 4, fontWeight: 500 }}>
            {label} {isRequired && <span style={{ color: "red" }}>*</span>}
          </p>
        )}
        <Controller
          name={name}
          control={control}
          rules={registerOptions}
          render={({ field: { onChange } }) => (
            <FormControl sx={{ m: 1, width: '100%' }}>
              <Select
                multiple
                IconComponent={FaChevronDown}
                value={selectedData}
                onChange={(e) =>
                  handleChange(e as SelectChangeEvent<string[]>, onChange)
                }
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (!Array.isArray(selected) || selected.length === 0)
                    return <em>{t("COMMON.SELECT_PROPERTY", { defaultValue: "اختر عقار" })}</em>
                  const chips: string[] = []
                  const remaining = new Set(selected as string[])
                  for (const group of categoryGroups) {
                    const catVals = group.propertyIds.map(String)
                    if (catVals.length > 0 && catVals.every((v) => remaining.has(v))) {
                      chips.push(group.categoryName)
                      catVals.forEach((v) => remaining.delete(v))
                    }
                  }
                  remaining.forEach((val) => {
                    const lbl = allProperties.find((p) => p.value === val)?.label || val
                    chips.push(lbl)
                  })
                  return chips.join(", ")
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                sx={{ direction: language === "ar" ? "rtl" : "ltr" }}
              >
                {categoryGroups.map((group) => {
                  const catVals = group.propertyIds.map(String)
                  const groupProps = allProperties.filter((p) =>
                    group.propertyIds.includes(Number(p.value))
                  )
                  if (groupProps.length === 0) return null
                  const allSelected =
                    catVals.length > 0 &&
                    catVals.every((v) => selectedData.includes(v))
                  const someSelected = catVals.some((v) =>
                    selectedData.includes(v)
                  )

                  return [
                    <MenuItem
                      key={`header-${group.categoryId}`}
                      disabled
                      sx={{
                        opacity: "1 !important",
                        fontWeight: 700,
                        fontSize: 11,
                        bgcolor: "#f5f3ff",
                        color: "#5b3fff",
                        py: 0.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      <span dir="auto">{group.categoryName}</span>
                    </MenuItem>,

                    groupProps.length > 1 && (
                      <MenuItem
                        key={`select-all-${group.categoryId}`}
                        value={`${SELECT_ALL_PREFIX}${group.categoryId}`}
                        sx={{ pl: 2, py: 0.5 }}
                      >
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          size="small"
                          sx={{ "&.Mui-checked": { color: "#5b3fff" } }}
                        />
                        <ListItemText
                          primary={t("COMMON.All")}
                          primaryTypographyProps={{
                            fontSize: 13,
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        />
                      </MenuItem>
                    ),

                    ...groupProps.map((prop) => (
                      <MenuItem
                        key={prop.value}
                        value={prop.value}
                        sx={{ pl: 3, py: 0.5 }}
                      >
                        <Checkbox
                          checked={selectedData.includes(prop.value)}
                          size="small"
                          sx={{ "&.Mui-checked": { color: "#5b3fff" } }}
                        />
                        <ListItemText
                          primary={prop.label}
                          primaryTypographyProps={{ fontSize: 13 }}
                        />
                      </MenuItem>
                    )),
                  ]
                })}

                {uncategorizedProps.length > 0 && [
                  <MenuItem
                    key="uncategorized-header"
                    disabled
                    sx={{
                      opacity: "1 !important",
                      fontWeight: 700,
                      fontSize: 11,
                      bgcolor: "#f5f3ff",
                      color: "#5b3fff",
                      py: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {t("CATEGORY.UNCATEGORIZED", { defaultValue: "غير مصنف" })}
                  </MenuItem>,
                  ...uncategorizedProps.map((prop) => (
                    <MenuItem
                      key={prop.value}
                      value={prop.value}
                      sx={{ pl: 3, py: 0.5 }}
                    >
                      <Checkbox
                        checked={selectedData.includes(prop.value)}
                        size="small"
                        sx={{ "&.Mui-checked": { color: "#5b3fff" } }}
                      />
                      <ListItemText
                        primary={prop.label}
                        primaryTypographyProps={{ fontSize: 13 }}
                      />
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          )}
        />
      </label>

      {helperText && (
        <div style={{ marginTop: 4, marginInline: 8, fontSize: 12, color: "#5b3fff", lineHeight: 1.6 }}>
          <span dir="rtl">{helperText.ar}</span>
          <span style={{ color: "#bbb", marginInline: 6 }}>|</span>
          <span dir="ltr">{helperText.en}</span>
        </div>
      )}

      {!withOutErrorMessage && (
        <div className="ErrorMessageStyle">
          <ErrorMessage
            errors={errors}
            name={name}
            render={({ message }) => <p>{message}</p>}
          />
        </div>
      )}
    </div>
  )
}
